// @ts-nocheck
import React, { useState } from 'react';
import { ReactComponent as WindIcon } from "../../../assets/icons/wind-02.svg";
import { ReactComponent as CloudRaining } from "../../../assets/icons/cloud-raining-05.svg";
import { ReactComponent as SunIcon } from "../../../assets/icons/sun.svg";
import { ReactComponent as TemperatureIcon } from "../../../assets/icons/thermometer-02.svg";
import { ReactComponent as HumidityIcon } from "../../../assets/icons/droplets-01.svg";
import { ReactComponent as ClockIcon } from "../../../assets/icons/clock-icon.svg";
import ColorPickerModal from './ColorPickerModal';

const CachedForecastStyleUpdate = ({ dataSourceRecord, onChange }) => {
  const [dataSourceRecordId] = Object.keys(dataSourceRecord);
  const record = dataSourceRecord[dataSourceRecordId];
  const { config, renderProps, resource } = record;
  
  // Hooks for both interface styles - must be declared unconditionally at the top level
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [currentParameter, setCurrentParameter] = useState(null);
  
  // These hooks are for the ProcessedModel-style interface
  const [variationsColorPickerOpen, setVariationsColorPickerOpen] = useState(false);
  const [currentChannel, setCurrentChannel] = useState(null);
  const [currentVariation, setCurrentVariation] = useState(null);
  
  // Parse the date issued from the resource
  const dateIssued = resource?.dateIssued ? new Date(resource.dateIssued) : new Date();
  
  // Map of variable keys to their icon components
  const iconComponents = {
    // Standard variable names
    Precipitation: CloudRaining,
    Wind80: WindIcon,
    Wind10: WindIcon,
    Wind: WindIcon,
    Solar: SunIcon,
    Temperature: TemperatureIcon,
    Humidity: HumidityIcon,
    
    // HRRR API response keys
    precip: CloudRaining,
    wind: WindIcon,
    temp: TemperatureIcon,
    humidity: HumidityIcon,
    solar: SunIcon
  };
  
  // Initialize render properties if they don't exist
  if (!renderProps.parameters) {
    // Create default render properties structure
    const parameters = {};
    
    // Create parameter objects for each available variable
    Object.keys(config.availableVariables || {}).forEach(variable => {
      parameters[variable] = {
        selected: variable === config.selectedVariable,
        color: renderProps.color || "#3B82F6",
        variations: {}
      };
      
      // Add variations for this parameter
      config.availableVariables[variable].variations.forEach(variation => {
        parameters[variable].variations[variation.key] = {
          selected: variation.key === config.selectedVariation,
          visible: true
        };
      });
    });
    
    // Update the record with the new structure
    onChange({
      [dataSourceRecordId]: {
        ...record,
        renderProps: {
          ...renderProps,
          parameters
        }
      }
    });
    
    return <div>Initializing forecast parameters...</div>;
  }
  
  // Convert older variations format to newer variations format if needed
  if (config.selectedVariable && !renderProps.variations && renderProps.parameters) {
    // Create ProcessedModel-like variations structure for compatibility
    const variations = {};
    
    // Get the currently selected parameter
    const selectedParamKey = config.selectedVariable;
    const selectedParam = renderProps.parameters[selectedParamKey];
    
    if (selectedParam) {
      // Each variation becomes a top-level entry in variations
      // First create a variations object for each variation in the parameter
      Object.entries(selectedParam.variations || {}).forEach(([variationKey, variationConfig]) => {
        if (variationConfig.selected) {
          // This is a selected variation, add it to our variations object
          variations[variationKey] = {
            // Each channel becomes a property in the variation
            // For now just create a placeholder "Value" channel
            "Value": {
              visible: true,
              color: selectedParam.color || "#3B82F6"
            }
          };
          
          // For Wind direction, add separate channels for sin/cos components
          if (variationKey === "Direction") {
            variations[variationKey]["u-component"] = {
              visible: true,
              color: "#4B5563"  // Gray color
            };
            variations[variationKey]["v-component"] = {
              visible: true, 
              color: "#EF4444"  // Red color
            };
          }
          
          // HRRR wind model
          if (variationKey === "wind10" || variationKey === "wind80") {
            variations[variationKey] = {
              "Speed": { visible: true, color: "#3B82F6" },  // Blue
              "Direction": { visible: true, color: "#F97316" }  // Orange
            };
          }
          
          // HRRR precip model
          if (variationKey === "Hourly" || variationKey === "Cumulative") {
            variations[variationKey] = {
              "Value": { visible: true, color: "#3B82F6" }  // Blue
            };
          }
          
          // HRRR solar model
          if (variationKey === "solarL" || variationKey === "solarS") {
            variations[variationKey] = {
              "Value": { visible: true, color: "#F59E0B" }  // Amber
            };
          }
          
          // For percentile forecasts (GEFS), add channels for each percentile
          if (config.modelType === "240hr3" || config.routeShort === "gefs_forecast") {
            variations[variationKey] = {
              "10th": { visible: true, color: "#3B82F6" },  // Blue
              "50th": { visible: true, color: "#10B981" },  // Green
              "90th": { visible: true, color: "#F97316" }   // Orange
            };
          }
        }
      });
    }
    
    // Update the record with the new structure
    if (Object.keys(variations).length > 0) {
      onChange({
        [dataSourceRecordId]: {
          ...record,
          renderProps: {
            ...renderProps,
            variations
          }
        }
      });
      
      return <div>Updating forecast parameters...</div>;
    }
  }
  
  // Function to select a parameter (variable)
  const selectParameter = (parameter) => {
    const updatedRenderProps = { ...renderProps };
    
    // Deselect all parameters first
    Object.keys(updatedRenderProps.parameters).forEach(param => {
      updatedRenderProps.parameters[param].selected = param === parameter;
    });
    
    // Select first variation of this parameter if none is selected
    const variations = Object.keys(updatedRenderProps.parameters[parameter].variations);
    if (variations.length > 0) {
      const hasSelectedVariation = variations.some(v => 
        updatedRenderProps.parameters[parameter].variations[v].selected
      );
      
      if (!hasSelectedVariation) {
        updatedRenderProps.parameters[parameter].variations[variations[0]].selected = true;
      }
    }
    
    // Update config with the selected values
    const selectedVariation = variations.find(v => 
      updatedRenderProps.parameters[parameter].variations[v].selected
    );
    
    onChange({
      [dataSourceRecordId]: {
        ...record,
        renderProps: updatedRenderProps,
        config: {
          ...record.config,
          selectedVariable: parameter,
          selectedVariation: selectedVariation
        }
      }
    });
  };
  
  // Function to select a variation for the currently selected parameter
  const selectVariation = (parameter, variation) => {
    const updatedRenderProps = { ...renderProps };
    
    // Deselect all variations for this parameter
    Object.keys(updatedRenderProps.parameters[parameter].variations).forEach(v => {
      updatedRenderProps.parameters[parameter].variations[v].selected = v === variation;
    });
    
    // Update the config
    onChange({
      [dataSourceRecordId]: {
        ...record,
        renderProps: updatedRenderProps,
        config: {
          ...record.config,
          selectedVariable: parameter,
          selectedVariation: variation
        }
      }
    });
  };
  
  // Function to change parameter color
  const openColorPicker = (parameter) => {
    setCurrentParameter(parameter);
    setColorPickerOpen(true);
  };
  
  // Function to handle color selection
  const handleColorSelect = (color) => {
    if (currentParameter) {
      const updatedRenderProps = { ...renderProps };
      updatedRenderProps.parameters[currentParameter].color = color;
      
      onChange({
        [dataSourceRecordId]: {
          ...record,
          renderProps: updatedRenderProps
        }
      });
    }
    setColorPickerOpen(false);
  };
  
  // Get available variables and variations
  const parameters = renderProps.parameters || {};
  
  // Get the currently selected parameter
  const selectedParameter = Object.keys(parameters).find(param => parameters[param].selected) || 
                            Object.keys(parameters)[0];
  
  // Get the available variations for the selected parameter
  const parameterVariations = selectedParameter ? 
                             config.availableVariables[selectedParameter]?.variations || [] : 
                             [];
  
  // If we have variations structure, show the ProcessedModel-style interface
  if (renderProps.variations && Object.keys(renderProps.variations).length > 0) {
    // Using the hooks declared at the top level
    
    // Function to toggle parameter visibility
    const toggleVisibility = (channel, variationKey) => {
      const updatedRenderProps = structuredClone(renderProps);
      updatedRenderProps.variations[variationKey][channel].visible = 
          !updatedRenderProps.variations[variationKey][channel].visible;

      onChange({
        [dataSourceRecordId]: {
          ...record,
          renderProps: updatedRenderProps
        }
      });
    }
    
    // Function to open color picker for a specific channel
    const openVariationsColorPicker = (channel, variationKey) => {
      setCurrentChannel(channel);
      setCurrentVariation(variationKey);
      setVariationsColorPickerOpen(true);
    }
    
    // Function to handle color selection for variations
    const handleVariationsColorSelect = (color) => {
      if (currentChannel && currentVariation) {
        const updatedRenderProps = structuredClone(renderProps);
        updatedRenderProps.variations[currentVariation][currentChannel].color = color;
        
        onChange({
          [dataSourceRecordId]: {
            ...record,
            renderProps: updatedRenderProps
          }
        });
      }
    }

    // Format variation key for display (Example: "wind_speed" => "Wind Speed")
    const formatVariationName = (key) => {
      return key.split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }

    // Function to toggle all parameters in a variation
    const toggleAllInVariation = (variationKey, setTo) => {
      const updatedRenderProps = structuredClone(renderProps);
      
      // Loop through all channels in this variation and set their visibility
      Object.keys(updatedRenderProps.variations[variationKey]).forEach(channel => {
        updatedRenderProps.variations[variationKey][channel].visible = setTo;
      });
      
      onChange({
        [dataSourceRecordId]: {
          ...record,
          renderProps: updatedRenderProps
        }
      });
    };
    
    // Function to check if all parameters in a variation are visible
    const areAllVisible = (renderData) => {
      return Object.values(renderData).every(item => item.visible);
    };
    
    // Function to check if none of the parameters in a variation are visible
    const areNoneVisible = (renderData) => {
      return Object.values(renderData).every(item => !item.visible);
    };

    // Function to render parameter buttons for a variation
    const renderParameterButtons = (variationKey, renderData) => {
      return (
        <div className="grid grid-cols-3 gap-1 mt-1 mb-2">
          {Object.entries(renderData).map(([channel, channelRender], index) => (
            <button
              key={`${variationKey}-${channel}-${index}`}
              onClick={() => toggleVisibility(channel, variationKey)}
              className={`px-2 py-1 border text-xs text-left rounded-md transition-colors ${
                channelRender.visible
                  ? 'bg-blue-50 border-blue-500 font-medium text-blue-700'
                  : 'bg-gray-50 border-gray-300 text-gray-500 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center justify-between">
                <span>{channel}</span>
                <button 
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent toggling visibility
                    openVariationsColorPicker(channel, variationKey);
                  }}
                  className="rounded-full w-5 h-5 flex items-center justify-center border border-gray-300 hover:border-gray-500 focus:outline-none cursor-pointer transition-all"
                  style={{ 
                    backgroundColor: channelRender.visible ? channelRender.color : '#e5e7eb'
                  }}
                  title="Change color"
                >
                </button>
              </div>
            </button>
          ))}
        </div>
      );
    }
    
    return (
      <div>
        <div className="mb-5">
          <h3 className="text-gray-700 font-medium text-sm mb-3">Date Information</h3>
          <div className="flex items-center py-2 px-3 bg-gray-50 rounded-md border border-gray-200">
            <ClockIcon className="h-5 w-5 mr-2 text-gray-600" />
            <div>
              <div className="text-sm font-medium">
                {dateIssued.toLocaleDateString('en-US', { 
                  weekday: 'short',
                  month: 'short', 
                  day: 'numeric', 
                  year: 'numeric'
                })}
              </div>
              <div className="text-xs text-gray-500">
                {dateIssued.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          </div>
        </div>
        
        <div className="space-y-2">
          <h3 className="text-gray-700 font-medium text-sm mb-2">
            Channels for {config.availableVariables[config.selectedVariable]?.name || config.selectedVariable}
          </h3>
          
          {Object.entries(renderProps.variations).map(([variationKey, renderData], index) => (
            <div key={`variation-${variationKey}-${index}`} className="border border-gray-200 rounded-md p-2 bg-white">
              <div className="flex justify-between items-center pb-1 border-b mb-1">
                <div className="font-medium text-sm text-gray-700">
                  {formatVariationName(variationKey)}
                </div>
                <div className="flex space-x-1">
                  <button 
                    onClick={() => toggleAllInVariation(variationKey, true)}
                    className={`text-xs px-2 py-0.5 rounded-md ${
                      areAllVisible(renderData) 
                        ? 'bg-blue-100 text-blue-700 font-medium' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    All
                  </button>
                  <button 
                    onClick={() => toggleAllInVariation(variationKey, false)}
                    className={`text-xs px-2 py-0.5 rounded-md ${
                      areNoneVisible(renderData) 
                        ? 'bg-blue-100 text-blue-700 font-medium' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    None
                  </button>
                </div>
              </div>
              {renderParameterButtons(variationKey, renderData)}
            </div>
          ))}
        </div>
        
        {/* Color Picker Modal */}
        {currentChannel && currentVariation && (
          <ColorPickerModal
            isOpen={variationsColorPickerOpen}
            onClose={() => setVariationsColorPickerOpen(false)}
            initialColor={renderProps.variations[currentVariation][currentChannel].color}
            onColorSelect={handleVariationsColorSelect}
          />
        )}
      </div>
    );
  }
  
  // Fall back to original interface if not using variations structure
  return (
    <div className="w-full">
      <div className="mb-5">
        <h3 className="text-gray-700 font-medium text-sm mb-3">Date Information</h3>
        <div className="flex items-center py-2 px-3 bg-gray-50 rounded-md border border-gray-200">
          <ClockIcon className="h-5 w-5 mr-2 text-gray-600" />
          <div>
            <div className="text-sm font-medium">
              {dateIssued.toLocaleDateString('en-US', { 
                weekday: 'short',
                month: 'short', 
                day: 'numeric', 
                year: 'numeric'
              })}
            </div>
            <div className="text-xs text-gray-500">
              {dateIssued.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        </div>
      </div>
      
      <div className="space-y-4">
        {/* Parameters Section */}
        <div className="border border-gray-200 rounded-md p-3 bg-white">
          <h3 className="text-gray-700 font-medium text-sm mb-2">Parameters</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {Object.keys(parameters).map(parameter => {
              const paramData = parameters[parameter];
              const Icon = iconComponents[parameter];
              
              return (
                <div 
                  key={parameter} 
                  className={`border rounded-md p-2 cursor-pointer flex items-center justify-between ${
                    paramData.selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => selectParameter(parameter)}
                >
                  <div className="flex items-center">
                    {Icon && <Icon className="h-5 w-5 mr-2 text-gray-600" />}
                    <span className={`${paramData.selected ? 'font-medium text-blue-700' : 'text-gray-700'}`}>
                      {config.availableVariables[parameter]?.name || parameter}
                    </span>
                  </div>
                  
                  <button 
                    className="rounded-full w-5 h-5 flex items-center justify-center border border-gray-300 hover:border-gray-500 focus:outline-none cursor-pointer transition-all"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent selecting the parameter
                      openColorPicker(parameter);
                    }}
                    style={{ backgroundColor: paramData.color }}
                    title="Change color"
                  >
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Variations Section */}
        {selectedParameter && (
          <div className="border border-gray-200 rounded-md p-3 bg-white">
            <h3 className="text-gray-700 font-medium text-sm mb-2">
              {config.availableVariables[selectedParameter]?.name || selectedParameter} Variations
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {parameterVariations.map(variation => {
                const variationData = parameters[selectedParameter].variations[variation.key] || {};
                const isSelected = variationData.selected;
                
                return (
                  <button
                    key={variation.key}
                    onClick={() => selectVariation(selectedParameter, variation.key)}
                    className={`text-left px-3 py-2 border rounded-md transition-colors ${
                      isSelected
                        ? 'bg-blue-50 border-blue-500 font-medium text-blue-700'
                        : 'bg-gray-50 border-gray-300 text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {variation.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      
      {colorPickerOpen && currentParameter && (
        <ColorPickerModal
          isOpen={colorPickerOpen}
          initialColor={parameters[currentParameter].color || "#3B82F6"}
          onColorSelect={handleColorSelect}
          onClose={() => setColorPickerOpen(false)}
        />
      )}
    </div>
  );
};

export default CachedForecastStyleUpdate;